import {productActivityLabel as productProcessingLabel,productActivityError as productProcessingError} from '@shared/productActivity';
import {allowsPhysicalProductStudio} from '@shared/productStudioOffering';
import {resolveOffering,type OfferingSource} from '@shared/offering';
import { ProductStudio } from '@/components/brands/ProductStudio';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { createLatestIdentityLoader } from '@/components/brands/identity-editor-state';
import { apiRequest } from '@/lib/queryClient';
import { IdentityEditor } from '@/components/brands/IdentityEditor';
import type { IdentitySettings } from '@shared/identity';
// Approved product identity with reviewable photo scan suggestions.

import { useState, useEffect, useRef } from "react";
import { useToast } from "@/hooks/use-toast";

interface ProductIdentityTabProps {
  brandId: number;
  embedded?: boolean;
  requestedProductId?: number | null;
  onProductOpened?: () => void;
  /** When provided, renders an "Add product" button that calls this. */
  onAddProduct?: () => void;
  /** When provided, each product card renders an Edit button. */
  onEditProduct?: (product: ProductRow) => void;
  /** When provided, each product card renders a Delete button. */
  onDeleteProduct?: (product: ProductRow) => void;
  /** Bump to force a reload (e.g. after a product was added in the parent). */
  refreshSignal?: number;
}

export type ProductRow = {
  id: number;
  identitySettings?: IdentitySettings | null;
  name: string;
  description: string | null;
  productUrl: string | null;
  colors: string[] | null;
  packagingDescription: string | null;
  visualAnalyzedAt: string | null;
  photos: string[] | null;
  productSheetUrl?: string | null;
  productSheetPanels?: unknown;
  productSheetAt?: string | Date | null;
  stateDescriptions?: string[] | null;
  sizeSpec?: string | null;
};

export function ProductIdentityTab({ brandId, onAddProduct, onEditProduct, onDeleteProduct, refreshSignal, embedded = false, requestedProductId, onProductOpened }: ProductIdentityTabProps) {
  const { toast } = useToast();
  const [brand,setBrand]=useState<OfferingSource|null>(null);
  const [products, setProducts] = useState<ProductRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [analyzingId, setAnalyzingId] = useState<number | null>(null);
  const [polls, setPolls] = useState(0);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [noteRequest, setNoteRequest] = useState<{id:number;field:string;value:string}>();
  const noteSequence = useRef(0);
  const selectedProduct = products.find(product => product.id === selectedId);

  useEffect(() => {
    if (requestedProductId == null || loading) return;
    if (products.some(product => product.id === requestedProductId)) {
      setSelectedId(requestedProductId);
      setDetailsOpen(true);
      setNoteRequest(undefined);
    } else {
      toast({title:'Product unavailable',description:'Refresh the product section and try again.',variant:'destructive'});
    }
    onProductOpened?.();
  }, [requestedProductId, products, loading, onProductOpened, toast]);

  const [loadError, setLoadError] = useState('');
  const loader = useRef(createLatestIdentityLoader());
  const currentBrand = useRef(brandId);
  const loadedBrandRef = useRef<number | null>(null);
  if (currentBrand.current !== brandId) {
    currentBrand.current = brandId;
    loader.current.invalidate();
  }
  const load = async (opts?: { silent?: boolean }) => {
    if (currentBrand.current !== brandId) return;
    if (!opts?.silent) setLoading(true);
    await loader.current.load(async () => {
      const [res,brandRes] = await Promise.all([apiRequest('GET', `/api/brands/${brandId}/products`),apiRequest('GET', `/api/brands/${brandId}`)]);
      const [rows,parentBrand] = await Promise.all([res.json(),brandRes.json()]);
      if (!Array.isArray(rows)) throw new Error('Unexpected product response');
      return {rows:rows as ProductRow[],brand:parentBrand as OfferingSource};
    }, result => { setProducts(result.rows);setBrand(result.brand); setLoadError(''); }, error => {
      setLoadError(error.message);
    }, () => setLoading(false));
  };
  useEffect(() => {
    setPolls(0);
    const brandChanged = loadedBrandRef.current !== brandId;
    loadedBrandRef.current = brandId;
    if (brandChanged) { setSelectedId(null); setProducts([]);setBrand(null); setLoadError(''); setAnalyzingId(null); }
    void load({ silent: !brandChanged });
    return () => { loader.current.invalidate(); };
  }, [brandId, refreshSignal]);

  // A freshly-added product analyzes in the background (~a few seconds). The
  // tab can load before that finishes and would otherwise sit on "not analyzed
  // yet" forever. Poll while any product has a photo but no colors yet, up to a
  // cap (so a genuinely-undetectable photo doesn't poll endlessly).
  useEffect(() => {
    const pending = products.some(
      (p) => (p.photos?.length ?? 0) > 0 && (!p.colors || p.colors.length === 0) && !p.identitySettings?.suggestions?.length,
    );
    const processing=products.some(p=>productProcessingLabel(p));
    if ((!pending&&!processing) || polls >= (processing?2400:10)) return;
    const t = setTimeout(() => {
      setPolls((n) => n + 1);
      load({ silent: true });
    }, 3000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products, polls]);

  useEffect(()=>{
    const refresh=()=>{if(document.visibilityState==='visible')void load({silent:true});};
    const identitySaved = (event: Event) => { if ((event as CustomEvent).detail?.brandId === brandId) void load({silent:true}); };
    window.addEventListener('admoji:identity-saved',identitySaved);
    window.addEventListener('focus',refresh);
    document.addEventListener('visibilitychange',refresh);
    return()=>{window.removeEventListener('admoji:identity-saved',identitySaved);window.removeEventListener('focus',refresh);document.removeEventListener('visibilitychange',refresh);};
  },[brandId]);

  const reanalyze = async (id: number) => {
    setAnalyzingId(id);
    try {
      const res = await apiRequest("POST", `/api/products/${id}/analyze-visual`);
      if (!res.ok) throw new Error("failed");
      if (currentBrand.current !== brandId) return;
      toast({ title: "Product suggestions ready", description: allowsPhysicalProductStudio(brand,products.find(product=>product.id===id))?"Review the proposed colors and packaging below. Approved values are unchanged.":"Review the proposed colors and offering details below. Approved values are unchanged." });
      // Silent: the row's own spinner (analyzingId) already reports progress —
      // blanking the whole tab to refresh one row is the v7.03 flicker.
      await load({ silent: true });
    } catch {
      if (currentBrand.current !== brandId) return;
      toast({ title: "Re-analyze failed", variant: "destructive" });
    } finally {
      if (currentBrand.current === brandId) setAnalyzingId(null);
    }
  };

  const physical=allowsPhysicalProductStudio(brand,selectedProduct);
  const software=resolveOffering(brand,selectedProduct).type==='software';
  const service=resolveOffering(brand,selectedProduct).type==='service';
  const brandType=resolveOffering(brand).type;
  const collection=brandType==='software'?'Platforms':brandType==='service'?'Services':brandType==='mixed'?'Offerings':'Products';
  const singular=brandType==='software'?'platform':brandType==='service'?'service':brandType==='mixed'?'offering':'product';
  if (loading || loadedBrandRef.current !== brandId) return <div className="p-6 text-sm text-slate-400">Loading products…</div>;

  return (
    <div className={embedded ? "space-y-5" : "p-5 space-y-5"} data-testid="product-identity-tab">
      {loadError && <div role="alert" className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700">Unable to refresh products. Existing edits are preserved. <button className="underline" onClick={() => load({silent:true})}>Retry</button><p className="text-xs mt-1">{loadError}</p></div>}
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-[15px] font-semibold text-[#1A1A1A]">
            {collection}
            {products.some(p=>productProcessingLabel(p)) && <span className="ml-2 text-[10px] font-normal text-slate-400">processing products…</span>}
          </h3>
          <p className="text-sm text-slate-500 mt-1">
            {allowsPhysicalProductStudio(brand)?'Open a product to view photos, improve its presentation, create multiple states, and edit its identity.':'Open an offering to review screenshots, reference images and approved identity.'}
          </p>
        </div>
        {onAddProduct && (
          <button
            onClick={onAddProduct}
            className="text-xs px-3 py-1.5 rounded bg-slate-900 text-white hover:bg-slate-800 flex-shrink-0"
          >
            + Add Offer
          </button>
        )}
      </div>

      {products.length === 0 ? (
        onAddProduct ? (
          <button
            onClick={onAddProduct}
            className="w-full rounded-lg border-2 border-dashed border-slate-200 py-8 text-center hover:border-slate-300"
            data-testid="button-products-empty-add"
          >
            <span className="block text-sm font-medium text-slate-500">{`No ${collection.toLowerCase()} yet`}</span>
            <span className="block text-xs text-slate-400 mt-1">+ Add Offer</span>
          </button>
        ) : (
          <p className="text-xs text-slate-400">{`No ${collection.toLowerCase()} yet.`}</p>
        )
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
          {products.map(p => <article key={p.id} className="rounded-xl border border-slate-200 bg-white overflow-hidden" data-testid={`product-card-${p.id}`}>
            <button type="button" className="w-full text-left group" aria-label={`Open ${p.name}`} onClick={() => {setSelectedId(p.id);setDetailsOpen(false);setNoteRequest(undefined);}}>
              <div className="relative h-52 bg-slate-50 p-4 flex items-center justify-center">{p.photos?.[0]?<img src={p.photos[0]} alt={p.name} className="w-full h-full object-contain group-hover:scale-[1.02] transition-transform"/>:<span className="text-sm text-slate-400">{resolveOffering(brand,p).type==='software'?'Add an interface screenshot':allowsPhysicalProductStudio(brand,p)?'Add a product photo':'Add a service reference image'}</span>}{productProcessingLabel(p)&&<div role="status" className="absolute inset-0 bg-black/25 flex items-center justify-center pointer-events-none"><span className="rounded-lg bg-black/65 px-3 py-2 text-sm font-medium text-white">{productProcessingLabel(p)}</span></div>}</div>
              <div className="p-4"><h3 className="font-semibold text-slate-900">{p.name}</h3><p className="text-xs text-slate-500 mt-1 line-clamp-2">{p.description||(resolveOffering(brand,p).type==='software'?'Interface screenshots and approved identity':'Photos, product states and approved identity')}</p><div className="flex items-center gap-1.5 mt-3">{(p.identitySettings?.palette?.map(color=>color.hex)??p.colors??[]).slice(0,6).map((hex,i)=><span key={i} title={hex} className="w-4 h-4 rounded-full border border-slate-200" style={{backgroundColor:hex}}/>)}<span className="text-[11px] text-slate-500 ml-1">{p.productSheetUrl&&allowsPhysicalProductStudio(brand,p)?'Product sheet ready':`${p.photos?.length??0} ${allowsPhysicalProductStudio(brand,p)?'photos':'reference images'}`}</span></div><span className="inline-block text-xs font-medium mt-3 text-slate-700">{resolveOffering(brand,p).type==='software'?'Open platform →':allowsPhysicalProductStudio(brand,p)?'Open product studio →':'Open service →'}</span>{productProcessingError(p)&&<p role="alert" className="mt-2 text-xs text-red-600">{productProcessingError(p)}</p>}</div>
            </button>
          </article>)}
        </div>
      )}
      <Dialog open={!!selectedProduct} onOpenChange={open=>{if(!open){setSelectedId(null);setPolls(0);void load({silent:true});}}}>
        <DialogContent className="w-[calc(100vw-32px)] max-w-5xl max-h-[92vh] overflow-y-auto rounded-2xl bg-white p-5 sm:p-7">
          {selectedProduct&&<>
            <div className="pr-8"><DialogTitle className="text-xl">{selectedProduct.name}</DialogTitle><DialogDescription className="mt-1">{physical?'Product photos, improved images and multiple states.':software?'App screenshots and reference images.':'Service reference images.'}</DialogDescription></div>
            <div className="flex gap-2 flex-wrap">{onEditProduct&&<button type="button" className="rounded-lg border px-3 py-2 text-sm" onClick={()=>{const product=selectedProduct;setSelectedId(null);onEditProduct(product);}}>{software?'Edit details & upload screenshots':service?'Edit details & upload references':'Edit details & upload photos'}</button>}{onDeleteProduct&&<button type="button" className="rounded-lg border px-3 py-2 text-sm text-red-600" onClick={()=>{const product=selectedProduct;setSelectedId(null);onDeleteProduct(product);}}>{software?'Delete platform':service?'Delete service':'Delete product'}</button>}</div>
            <ProductStudio key={selectedProduct.id} product={selectedProduct} brand={brand} onJobChange={()=>{setPolls(0);void load({silent:true});}} onAssetsUpdated={assets=>{loader.current.invalidate();setProducts(items=>items.map(item=>item.id===selectedProduct.id?{...item,...assets}:item));}} onAddSuggestedState={note=>{setDetailsOpen(true);setNoteRequest({id:++noteSequence.current,field:'stateDescriptions',value:note});}}/>
            <details open={detailsOpen} onToggle={e=>setDetailsOpen(e.currentTarget.open)} className="border-t pt-5"><summary className="cursor-pointer text-sm font-semibold">{software?'Platform identity & business details':service?'Service identity & business details':'Product identity & business details'}</summary><div className="space-y-4 mt-4"><div className="flex items-center justify-between gap-3"><p className="text-xs text-slate-500">{physical?'Review approved colors, packaging and product state notes.':'Review approved colors and offering details.'}</p><button type="button" className="rounded-lg border px-3 py-2 text-xs shrink-0" disabled={analyzingId===selectedProduct.id} onClick={()=>reanalyze(selectedProduct.id)}>{analyzingId===selectedProduct.id?'Scanning…':!physical?'Scan reference for identity':'Scan photo for identity'}</button></div><IdentityEditor record={selectedProduct} brand={brand} kind="products" listAppendRequest={noteRequest} onUpdated={updated=>{loader.current.invalidate();setProducts(items=>items.map(item=>item.id===updated.id?updated:item));}}/></div></details>
          </>}
        </DialogContent>
      </Dialog>
    </div>
  );
}
